import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
// ngrx store
import { Store } from '@ngrx/store';
import { interval, Subscription } from 'rxjs';
import { map, take, takeWhile } from 'rxjs/operators';

// services
import { LoginService } from '../../../shared/services/login.service';
import { ErrorHandler } from '../../../shared/services/error-handler.service';
import { CopyService } from '../../../shared/services/app-copy.service';
import { DataStorageService } from "../../../shared/services/data-storage.service";

// models
import { AppState } from '../../../booking/models/app-state.interface';
// globals
import { EMAIL_PATTERN } from '../../../shared/globals';

@Component({
  selector: 'app-validate-email',
  templateUrl: './validate-email.component.html',
  styleUrls: ['./validate-email.component.scss'],
  providers: [LoginService]
})
export class ValidateEmailComponent implements OnInit, OnDestroy {

  private _subscription: Subscription;
  private _userSubscription: Subscription;
  private _userLanguageSubscription: Subscription;
  private _bookingInitSubscription: Subscription;
  private _validateOTPCodeSubscription: Subscription;
  private _userOTPSubscription: Subscription;
  private _dataSavedSubscription; Subscription;

  public emailPattern = EMAIL_PATTERN;
  public showEmailSent: Boolean = false;
  public email: string;
  public linkExpired: Boolean = false;
  public showLoader: Boolean = false;
  public userEmail;
  public appCopy;
  public copyLoaded: Boolean = false;
  public selectedLanguage = '';
  public countdown = 1;
  public isCounting: boolean = false;
  public userHasDetails: boolean = false;
  public is2FARequired: boolean = false;

  constructor(
    private _appStore: Store<AppState>,
    private _dataStorageService: DataStorageService,
    private _loginService: LoginService,
    private _router: Router,
    private _errorHandler: ErrorHandler,
    private _copyService: CopyService,
  ) { }

  ngOnInit() {

    this._userLanguageSubscription = this._appStore.select('copyReducer')
      .subscribe((res: any) => {
        this.selectedLanguage = res.language
      })

    this._bookingInitSubscription = this._appStore.select('loadedStatusReducer')
      .subscribe((res: any) => {
        this.copyLoaded = res.copyLoaded;
        if (res.copyLoaded) {
          this.getCopy();
        }

      })

    this._userSubscription = this._appStore.select('userReducer')
      .subscribe((user: any) => {

        if (user.userDetails.userEmail === '') {
          this._router.navigate(['login']);
        }

        this.is2FARequired = user.userDetails.is2FARequired;
        this.userEmail = user.userDetails.userEmail;
        this.userHasDetails = user.userDetails.userHasAccountDetails;
        this.linkExpired = user.userDetails.userEmailTokenValid;
      });

    this._subscription = this._loginService.forgotPasswordEmailSent$
      .subscribe(emailSent => {
        if (emailSent.error) {
          this.handleError();
          return;
        }

        if (emailSent) {
          const data = {
            text: "OTP sent to Email!",
            type: "info",
          };

          if (this.getState(this._appStore).userReducer.userDetails.is2FARequired) {
            data.text = 'OTP sent to phone number!';
          }
          this._errorHandler.handleCustomInfo(data);
        }

        this.showLoader = false;
        this.showEmailSent = emailSent;
        this.startCountDown();
      },
        err => console.log(err));

    this._validateOTPCodeSubscription = this._loginService.validateOTPCode$
      .subscribe(res => {

        if (res.error) {
          this.showLoader = false;
          return;
        } else {
          // this._router.navigate(['set-password']);
        }

        //  admin case4
        if(res && !res.token){
          this._router.navigate(['set-password']);
        }

      },
        err => console.log(err));

    this._userOTPSubscription = this._loginService.userSubject$
      .subscribe(user => {
        if (user.error) {
          this.handleError();
          return;
        }

        this._appStore.dispatch({ type: 'USER_ACCOUNT_UPDATE_STATUS', payload: !this.userHasDetails });

        // save data and resirect
        this._dataStorageService.saveData();

      },
        err => console.log(err));

    this._dataSavedSubscription = this._dataStorageService.dataSaved$.subscribe(
      (res) => {
        if (res) {
          this.showLoader = false;

          // redirect after data saved
          if(this.userHasDetails){
            this._router.navigate(["my-account/home"]);
          } else {
            this._router.navigate(['account-details']);
          }
        }
      }
    );

  }

  getCopy() {
    const panelHeaderCopy = this._copyService.getCopyBySection(
      'OTHER',
      'emailsent',
      ['showEmailSent']
    );
    console.log(panelHeaderCopy)
    this.appCopy = {
      panelHeader: panelHeaderCopy[0],
    };

  }

  // get store state
  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1))
      .subscribe(s => state = s);
    return state;
  }

  handleError() {
    this.showLoader = false;
    this._errorHandler.handleCustomInfo({
      text: 'Something went wrong!',
      type: 'info'
    });
  }

  handleSubmit(value, event) {
    event.preventDefault();
    this.showLoader = true;

    if (value.otp) {
      this.handleSubmitOTP(value);
      return
    }

    this._loginService.sendOTP(this.userEmail);
  }

  resendEmail(e) {
    e.preventDefault();
    const token = this.getState(this._appStore).userReducer.userDetails.userToken;
    // if(this.is2FARequired) {
    //   this._loginService.SendOneTimeLoginCode(this.userEmail, 'mmb-login', token);
    // } else {
      this._loginService.sendOTP(this.userEmail);
    // }
  }

  goToLogin() {
    this._router.navigate(['access-options']);
  }

  handleSubmitOTP(value) {
    this._loginService.validateOTPCode(value.otp, value.email, 'mmb-login', this.is2FARequired, 'validateEmailScreen');
  }

  startCountDown() {
    if (this.isCounting) return; // Prevent multiple intervals
    this.isCounting = true;
    this.countdown = 60;

    interval(1000) // Emit every second
      .pipe(
        takeWhile(() => this.countdown > 1),  // Stop when countdown reaches 1
        map(() => --this.countdown) // Decrease countdown by 1
      )
      .subscribe({
        complete: () => {
          this.isCounting = false; // Reset state after countdown ends
          this.countdown = 1; // Ensure countdown ends at 0
        }
      });
  }

  ngOnDestroy() {
    this._dataSavedSubscription.unsubscribe();
    this._validateOTPCodeSubscription.unsubscribe();
    this._subscription.unsubscribe();
    this._userSubscription.unsubscribe();
    this._userLanguageSubscription.unsubscribe();
    this._bookingInitSubscription.unsubscribe();
    this._userOTPSubscription.unsubscribe();
    this._dataSavedSubscription.unsubscribe();
    this._validateOTPCodeSubscription.unsubscribe();
  }

}
